// motor/pipeline.js
import { limpiarBasuraXml } from '../preproceso/limpiarBasuraXml.js'
import { reconstruirPlaceholders } from '../preproceso/reconstruirPlaceholders.js'
import { expandirRepiteEstructura } from '../preproceso/repiteEstructura.js'
import { expandirTablas } from '../preproceso/expandirTablas.js'
import { extraerExpresiones } from '../lectura/extraerExpresiones.js'
import { normalizarPlaceholders } from '../normalizacion/normalizarPlaceholders.js'
import { ejecutarExpresion } from './ejecutarExpresion.js'
import { reemplazarTextoPlano } from '../resolucion/reemplazarTextoPlano.js'
import { resolverSaltosPagina } from '../resolucion/resolverSaltosPagina.js'
import {
  crearContextoImagenes,
  finalizarContextoImagenes,
  resolverImagenes,
} from '../resolucion/resolverImagenes.js'
import { obtenerPartesTextoDocx } from '../docx/partes.js'
import PizZip from 'pizzip'

function registrarPlaceholderOriginal(mapa, normalizado, original) {
  if (!mapa.has(normalizado)) mapa.set(normalizado, [])
  mapa.get(normalizado).push(original)
}

/**
 * Procesa un DOCX completo aplicando:
 * → reconstrucción de tags
 * → normalización
 * → resolución de variables v: e i:
 * → funciones personalizadas
 * → reemplazo de imágenes base64
 */
export async function procesarDocx(blob, variables, expresiones, datos) {
  const isDev = process.env.NODE_ENV !== 'production'
  const errors = []
  const arrayBuffer = await blob.arrayBuffer()
  const zip = new PizZip(arrayBuffer)
  const runtimeContext = { variables, expresiones }
  const archivos = obtenerPartesTextoDocx(zip)
  const originalesTextoPorParte = new Map()
  const originalesImagenPorParte = new Map()

  // =======================
  // 1️⃣ Preprocesado del XML
  // =======================
  for (const archivo of archivos) {
    const file = zip.file(archivo)

    let xml = file.asText()

    xml = limpiarBasuraXml(xml)
    xml = reconstruirPlaceholders(xml)
    xml = expandirRepiteEstructura(xml, datos, variables, expresiones, errors)
    xml = expandirTablas(xml, datos, variables, expresiones, errors)

    zip.file(archivo, xml)
  }
  // ===================================
  // 2️⃣ Detectar y normalizar placeholders
  // ===================================
  for (const archivo of archivos) {
    const file = zip.file(archivo)

    let xml = file.asText()
    const placeholders = extraerExpresiones(xml)
    if (isDev) {
      console.debug('[formatos] placeholders', archivo, placeholders.map((p) => p.tag))
    }

    const { mapaFinal, errores: erroresNormalizacion } = normalizarPlaceholders(
      placeholders,
      variables,
      expresiones,
      errors
    )
    for (const error of erroresNormalizacion) {
      errors.push({
        code: 'placeholder_syntax_invalid',
        message: error.error,
        detail: { tag: error.tag, partName: archivo },
      })
    }
    if (isDev) {
      console.debug('[formatos] mapaFinal', archivo, mapaFinal)
    }

    // Reemplazar por expresiones normalizadas
    const originalesTexto = new Map()
    const originalesImagen = new Map()
    for (const ph of placeholders) {
      const normalizado = mapaFinal[ph.tag]
      const contenidoNormalizado = normalizado.replace(/^\$\{|\}$/g, '').trim()
      registrarPlaceholderOriginal(
        contenidoNormalizado.startsWith('i:') ? originalesImagen : originalesTexto,
        normalizado,
        ph.tag
      )
      xml = xml.replace(ph.tag, normalizado)
    }
    originalesTextoPorParte.set(archivo, originalesTexto)
    originalesImagenPorParte.set(archivo, originalesImagen)

    zip.file(archivo, xml)
  }
  // ============================================
  // 3️⃣ Resolver variables y funciones de texto
  // ============================================
  for (const archivo of archivos) {
    const file = zip.file(archivo)

    let xml = file.asText()

    // REEMPLAZO PROPIO
    xml = reemplazarTextoPlano(xml, datos, errors, {
      ...runtimeContext,
      placeholdersOriginales: originalesTextoPorParte.get(archivo),
    })
    xml = resolverSaltosPagina(xml)

    zip.file(archivo, xml)
  }
  // ===================================
  // 4️⃣ Resolver imágenes i:
  // ===================================
  const contextoImagenes = crearContextoImagenes(zip, archivos, originalesImagenPorParte)
  for (const archivo of archivos) {
    const file = zip.file(archivo)

    let xml = file.asText()
    xml = resolverImagenes(xml, datos, zip, archivo, errors, contextoImagenes)
    zip.file(archivo, xml)
  }
  finalizarContextoImagenes(zip, contextoImagenes)

  // ===================================================
  // 5️⃣ Generar DOCX final
  // ===================================================
  const resultado = { blob: zip.generate({ type: 'blob' }), errors }
  if (errors.length) {
    console.warn('[formatos] El documento se generó con incidencias', errors)
  }
  return resultado
}
